import { EVENT5K } from "@/constants/testIds";
import { PRICING_TABLE, SHIRT_INFO, goToRunSignup } from "@/lib/runsignup";
import { Check, CalendarClock, ShoppingCart, Users2, Heart, ExternalLink, Zap } from "lucide-react";

// Determine which tier is currently active based on today's date.
function currentTierKey() {
  const now = Date.now();
  const earlyEnd = new Date("2026-09-23T04:00:00Z").getTime();
  const regularStart = new Date("2026-10-28T04:00:00Z").getTime();
  if (now < earlyEnd) return "early_bird";
  if (now < regularStart) return "standard";
  return "regular";
}

export default function Pricing() {
  const activeKey = currentTierKey();

  return (
    <section
      id="pricing"
      data-testid={EVENT5K.sectionPricing}
      className="relative bg-[#0A0A0A] py-24 md:py-32 border-t border-[#27272A]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#00A651] font-bold mb-4">Registration Pricing</div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white">
            Lock in your <span className="text-[#EC008C]">best price</span>.
          </h2>
          <p className="mt-4 text-lg text-[#A3A3A3]">
            The earlier you register, the more you save. Every dollar fuels entrepreneurs.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 border border-[#00A651]/50 bg-[#00A651]/10 px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#00A651] font-bold">
            <ExternalLink className="w-3.5 h-3.5" />
            All registration &amp; checkout on RunSignup
          </div>
        </div>

        {/* Two race categories side-by-side */}
        <div className="mt-12 grid lg:grid-cols-2 gap-6 md:gap-8">
          {Object.entries(PRICING_TABLE).map(([raceKey, race]) => (
            <div key={raceKey} className="bg-[#141414] border border-[#27272A] p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#EC008C] font-bold">
                    Choose your race
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl font-black uppercase text-white leading-none mt-1">
                    {race.label}
                  </h3>
                </div>
                <div
                  className="font-display text-4xl md:text-5xl font-black leading-none"
                  style={{ color: raceKey === "5k" ? "#00A651" : "#EC008C" }}
                >
                  {raceKey === "5k" ? "3.1mi" : "1mi"}
                </div>
              </div>

              <div className="space-y-3">
                {race.tiers.map((t) => {
                  const active = t.key === activeKey;
                  return (
                    <div
                      key={t.key}
                      className={`relative p-4 border-2 transition-colors ${active ? "border-[#00A651] bg-[#00A651]/5" : "border-[#27272A] bg-black/40"}`}
                    >
                      {active && (
                        <div className="absolute -top-2.5 left-3 bg-[#00A651] text-black text-[9px] uppercase tracking-[0.24em] font-black px-2 py-0.5 flex items-center gap-1">
                          <Zap className="w-3 h-3" /> Live Now
                        </div>
                      )}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                        <div>
                          <div className="font-display text-lg font-bold uppercase text-white leading-tight">
                            {t.label}
                          </div>
                          <div className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#EC008C]">
                            <CalendarClock className="w-3 h-3" />
                            {t.dates}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <PriceCell label="No Shirt" value={t.base} />
                          <PriceCell label="+ Shirt" value={t.withShirt} accent="#EC008C" />
                          <PriceCell label="2XL / 3XL" value={t.plusSize} muted />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => goToRunSignup(raceKey === "5k" ? "register5K" : "register1Mile")}
                data-testid={`register-${raceKey}-btn`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-sm py-4 transition-all hover:-translate-y-0.5"
              >
                <ShoppingCart className="w-4 h-4" />
                Register {race.label} on RunSignup
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        {/* RunSignup explainer block */}
        <div className="mt-6 bg-black border border-[#EC008C]/40 p-5 md:p-6">
          <p className="text-sm md:text-base text-[#E5E5E5] leading-relaxed">
            <strong className="text-[#EC008C]">Registration and checkout are processed securely through RunSignup.</strong>{" "}
            Click below to register for the 5K Run/Walk, 1-Mile Fun Run, create or join a team, add an optional event
            shirt, or make a donation.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => goToRunSignup("register")}
              className="bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs md:text-sm px-5 py-3 transition-all"
            >
              Register Now →
            </button>
            <button
              onClick={() => goToRunSignup("team")}
              className="border-2 border-white/30 hover:border-[#EC008C] hover:text-[#EC008C] text-white font-bold uppercase tracking-widest text-xs md:text-sm px-5 py-3 transition-all inline-flex items-center gap-2"
            >
              <Users2 className="w-4 h-4" />
              Create or Join a Team
            </button>
            <button
              onClick={() => goToRunSignup("donate")}
              className="border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-xs md:text-sm px-5 py-3 transition-all inline-flex items-center gap-2"
            >
              <Heart className="w-4 h-4" />
              Donate
            </button>
          </div>
        </div>

        {/* Shirt info banner */}
        <div className="mt-4 bg-gradient-to-r from-[#EC008C]/15 via-[#00A651]/10 to-[#EC008C]/15 border-2 border-[#EC008C] p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#EC008C] flex items-center justify-center anim-bounce-slow shrink-0">
              <span className="font-display text-xl font-black text-white">👕</span>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#EC008C] font-black">Optional Event Shirt</div>
              <div className="font-display text-lg md:text-xl font-bold uppercase text-white leading-tight">
                Youth &ndash; Adult XL: <span className="text-[#00A651]">+${SHIRT_INFO.youthToXL}</span> · Adult 2XL &amp; 3XL: <span className="text-[#00A651]">+${SHIRT_INFO.plusSize}</span>
              </div>
              <div className="text-xs text-[#A3A3A3] mt-1">
                Shirts are guaranteed for registrations completed by <strong className="text-white">{SHIRT_INFO.deadline}</strong>. Sizes and availability are not guaranteed after this date.
              </div>
            </div>
          </div>
        </div>

        <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            "Register on RunSignup",
            "Create or join a team",
            "Add an optional event shirt",
            "Donate through RunSignup",
          ].map((item) => (
            <li key={item} className="flex items-center gap-2 bg-[#141414] border border-[#27272A] px-4 py-3 text-sm text-white">
              <Check className="w-4 h-4 text-[#00A651]" /> {item}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function PriceCell({ label, value, accent, muted }) {
  return (
    <div className="text-right md:text-left">
      <div
        className={`font-display text-2xl md:text-3xl font-black leading-none ${muted ? "text-[#A3A3A3]" : ""}`}
        style={{ color: accent && !muted ? accent : muted ? undefined : "#FFFFFF" }}
      >
        ${value}
      </div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#A3A3A3] mt-0.5">{label}</div>
    </div>
  );
}
